/**
 * Natural sort comparison for OS-like ordering of names.
 *
 * Handles mixed text and numbers correctly:
 *   - Special characters first (e.g., "_hello")
 *   - Numbers in numeric order (e.g., "2. Hello" before "10. Hello")
 *   - Alphabetical after that
 *   - Case-insensitive
 *
 * Usage:
 *   items.sort((a, b) => naturalCompare(a.name, b.name));
 *   items.sort((a, b) => naturalCompare(a.name, b.name) * (sortDir === 'asc' ? 1 : -1));
 */

const NATURAL_SPLIT_RE = /(\d+)/;

/**
 * Compare two strings using natural (OS-like) sort order.
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns Negative if a < b, positive if a > b, 0 if equal
 */
export function naturalCompare(a: string | null | undefined, b: string | null | undefined): number {
  // Handle null/undefined — sort them last
  const sa = (a ?? '').trim();
  const sb = (b ?? '').trim();

  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;

  // Category (OS-like ordering):
  //   0 = starts with special char (non-alphanumeric)
  //   1 = starts with digit
  //   2 = starts with letter
  const catOf = (c: string) => /[0-9]/.test(c) ? 1 : /[a-zA-Z\p{L}]/u.test(c) ? 2 : 0;
  const catA = catOf(sa[0]);
  const catB = catOf(sb[0]);

  if (catA !== catB) return catA - catB;

  // Split into text and numeric segments
  const partsA = sa.toLowerCase().split(NATURAL_SPLIT_RE);
  const partsB = sb.toLowerCase().split(NATURAL_SPLIT_RE);

  const len = Math.min(partsA.length, partsB.length);

  for (let i = 0; i < len; i++) {
    const pa = partsA[i];
    const pb = partsB[i];

    if (pa === pb) continue;

    // Check if both segments are numeric
    const numA = /^\d+$/.test(pa) ? parseInt(pa, 10) : NaN;
    const numB = /^\d+$/.test(pb) ? parseInt(pb, 10) : NaN;

    if (!isNaN(numA) && !isNaN(numB)) {
      // Both numeric — compare as numbers
      if (numA !== numB) return numA - numB;
    } else {
      // At least one is text — compare as strings (locale-aware for i18n)
      const cmp = pa.localeCompare(pb, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
    }
  }

  // If all shared segments match, shorter one comes first
  return partsA.length - partsB.length;
}
